import React, { useEffect, useState } from "react";
import AlumniLayout from "@/components/AlumniLayout";
import { BookOpen, ArrowLeft, ExternalLink, Plus, Trash2, X, Loader2, ImagePlus } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/lib/branding";
import { toast } from "sonner";

const embedUrl = (e) => `https://e.issuu.com/embed.html?d=${e.issuu_doc}&u=${e.issuu_user}`;
const issuuUrl = (e) => `https://issuu.com/${e.issuu_user}/docs/${e.issuu_doc}`;
const coverSrc = (e) => (e.cover_path ? fileUrl(e.cover_path) : e.cover_url || "");

export default function TowerMagazine() {
  const { isAdmin } = useAuth();
  const b = useBranding();
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/magazine/editions");
      setEditions(data || []);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (ed) => {
    if (!window.confirm(`Delete "${ed.title}"?`)) return;
    try {
      await api.delete(`/magazine/editions/${ed.id}`);
      setEditions((list) => list.filter((x) => x.id !== ed.id));
      toast.success("Edition removed");
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  if (reading) {
    return (
      <AlumniLayout wide>
        <button data-testid="tower-back" onClick={() => setReading(null)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4" /> All issues
        </button>
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-serif text-2xl tracking-tight">{reading.title}</h1>
          <a data-testid="tower-open-issuu" href={issuuUrl(reading)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-primary hover:underline">
            Open on issuu <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm" style={{ aspectRatio: "16 / 10" }}>
          <iframe
            data-testid="tower-reader"
            title={reading.title}
            src={embedUrl(reading)}
            allow="fullscreen"
            allowFullScreen
            className="w-full h-full"
            style={{ border: 0 }}
          />
        </div>
      </AlumniLayout>
    );
  }

  return (
    <AlumniLayout wide>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{b.university_name}</div>
          <h1 className="font-serif text-3xl tracking-tight mt-1">Tower Magazine</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Read the Tower magazine — stories, features and updates from across the alumni community.</p>
        </div>
        {isAdmin && (
          <button data-testid="tower-add-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90 transition-opacity">
            <Plus className="h-4 w-4" /> Add issue
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-16 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading issues…</div>
      ) : editions.length === 0 ? (
        <div className="text-center text-muted-foreground text-sm py-16 border border-dashed border-border rounded-lg">No issues yet.{isAdmin ? " Click \u201cAdd issue\u201d to publish one." : ""}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {editions.map((e) => (
            <div key={e.id} data-testid={`tower-card-${e.id}`} className="group relative">
              <button onClick={() => setReading(e)} className="block w-full text-left rounded-lg overflow-hidden shadow-md ring-1 ring-black/5 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="aspect-[3/4] bg-secondary/10 overflow-hidden flex items-center justify-center relative">
                  {coverSrc(e) ? (
                    <img src={coverSrc(e)} alt={`${e.title} cover`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground"><BookOpen className="h-12 w-12" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 text-navy px-2.5 py-1 text-xs font-semibold shadow opacity-0 group-hover:opacity-100 transition-opacity">
                    <BookOpen className="h-3.5 w-3.5" /> Read now
                  </span>
                </div>
              </button>
              <div className="mt-2 px-0.5">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{e.term}</div>
                <div className="font-serif text-sm tracking-tight leading-tight">{e.title}</div>
              </div>
              {isAdmin && (
                <button data-testid={`tower-delete-${e.id}`} onClick={() => remove(e)} title="Delete issue" className="absolute top-2 right-2 rounded-full bg-white/95 text-red-600 p-1.5 shadow hover:bg-white transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {adding && <AddEditionModal onClose={() => setAdding(false)} onSaved={(ed) => { setEditions((l) => [...l, ed]); setAdding(false); }} />}
    </AlumniLayout>
  );
}

function AddEditionModal({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [term, setTerm] = useState("");
  const [issuuLink, setIssuuLink] = useState("");
  const [coverPath, setCoverPath] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPickCover = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverPath(data.path);
      setCoverPreview(fileUrl(data.path));
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!issuuLink.trim()) return toast.error("Issuu link is required");
    if (!coverPath) return toast.error("Please upload a cover image");
    setSaving(true);
    try {
      const { data } = await api.post("/magazine/editions", {
        title: title.trim(), term: term.trim(), issuu_url: issuuLink.trim(), cover_path: coverPath,
      });
      toast.success("Issue published");
      onSaved(data);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div data-testid="tower-add-modal" className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg mt-10 mb-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-serif text-lg">Add a Tower issue</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <input data-testid="tower-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tower — Fall 2026" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Term (optional)</label>
              <input data-testid="tower-term-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Fall 2026" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Issuu link</label>
            <input data-testid="tower-issuu-input" value={issuuLink} onChange={(e) => setIssuuLink(e.target.value)} placeholder="https://issuu.com/udallas/docs/udallas_tower_fall_2026" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            <p className="text-[11px] text-muted-foreground mt-1">Paste the full Issuu link (issuu.com/&lt;user&gt;/docs/&lt;document&gt;) or an Issuu embed link.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cover image</label>
            <div className="mt-1 flex items-center gap-3">
              <div className="h-28 w-21 aspect-[3/4] rounded-md border border-border bg-secondary/10 overflow-hidden flex items-center justify-center shrink-0" style={{ width: "5.25rem" }}>
                {coverPreview ? <img src={coverPreview} alt="cover preview" className="w-full h-full object-cover" /> : <ImagePlus className="h-6 w-6 text-muted-foreground" />}
              </div>
              <label className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-secondary/10">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {coverPreview ? "Change image" : "Upload image"}
                <input data-testid="tower-cover-input" type="file" accept="image/*" className="hidden" onChange={onPickCover} disabled={uploading} />
              </label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm border border-border hover:bg-secondary/10">Cancel</button>
          <button data-testid="tower-save-btn" onClick={save} disabled={saving || uploading} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Publish issue
          </button>
        </div>
      </div>
    </div>
  );
}
